
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, Menu, Home, Info, Package, Wrench, Store, Calendar, BookOpen, Mail, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useUserRole } from "@/hooks/useUserRole";
import { useProfileCompletion } from "@/hooks/useProfileCompletion";

const NavBar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isAdmin, role } = useUserRole();
  const { isProfileComplete } = useProfileCompletion();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/account");
    setIsOpen(false);
  };

  const handleNavClick = () => {
    setIsOpen(false);
  };


  const isEmployee = role === 'moderator';
  const hasOrderAccess = isAdmin;
  const hasProductAccess = isAdmin;
  const hasContentAccess = isAdmin || role === 'moderator';

  return (
    <header className="w-full border-b bg-white shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <Link to="/home" className="flex items-center space-x-2">
            <img 
              src="/lovable-uploads/agroref_fav.ico"
              alt="Agroref Logo"
              className="h-10 w-10"
            />
            <span className="text-xl font-bold text-zinc-900">Agroref</span>
          </Link>
          
          <nav className="hidden lg:flex items-center space-x-1 flex-1 justify-center">
            <Link to="/home" className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors whitespace-nowrap">
              Accueil
            </Link>
            <Link to="/a-propos" className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors whitespace-nowrap">
              À Propos
            </Link>
            
            <DropdownMenu>
              <DropdownMenuTrigger className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors inline-flex items-center gap-1 whitespace-nowrap">
                Produits
                <span className="text-xs">▼</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#2c2c2c] border-[#2c2c2c] z-50 min-w-[200px] p-1">
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/produits/lely" className="w-full flex items-center h-10 px-3">Lely</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/produits/boumatic" className="w-full flex items-center h-10 px-3">BouMatic</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/produits/mueller" className="w-full flex items-center h-10 px-3">Mueller</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/produits/liberchem" className="w-full flex items-center h-10 px-3">Liberchem</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors inline-flex items-center gap-1 whitespace-nowrap">
                Services
                <span className="text-xs">▼</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#2c2c2c] border-[#2c2c2c] z-50 min-w-[240px] p-1">
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/services/assistance" className="w-full flex items-center h-10 px-3">Assistance à la traite</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/services/inspection" className="w-full flex items-center h-10 px-3">Inspection du système de traite</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/services/pro-action" className="w-full flex items-center h-10 px-3">Rapports Pro Action</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/services/urgence" className="w-full flex items-center h-10 px-3">Service d'urgence 24/7</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <a href="https://agrostitch.ca/collections" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors whitespace-nowrap">
              Boutique
            </a>

            <Link to="/projets" className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors whitespace-nowrap">
              Projet et Événements
            </Link>
            <Link to="/blogue" className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors whitespace-nowrap">
              Blogue
            </Link>
          </nav>

          <div className="flex items-center space-x-3">
            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[88vw] max-w-[340px] bg-white p-0 flex flex-col">
                <SheetHeader className="px-5 py-4 border-b bg-gradient-to-r from-zinc-50 to-white">
                  <SheetTitle className="flex items-center gap-2">
                    <img src="/lovable-uploads/agroref_fav.ico" alt="Agroref" className="h-7 w-7" />
                    <span className="text-lg font-bold text-zinc-900">Agroref</span>
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
                  <Link
                    to="/home"
                    onClick={handleNavClick}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-900 hover:bg-accent/10 hover:text-accent transition-colors"
                  >
                    <Home className="h-4 w-4" />
                    Accueil
                  </Link>
                  <Link
                    to="/a-propos"
                    onClick={handleNavClick}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-900 hover:bg-accent/10 hover:text-accent transition-colors"
                  >
                    <Info className="h-4 w-4" />
                    À Propos
                  </Link>

                  <details className="group rounded-lg">
                    <summary className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-900 hover:bg-accent/10 hover:text-accent transition-colors cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                      <Package className="h-4 w-4" />
                      <span className="flex-1">Produits</span>
                      <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="mt-1 ml-7 pl-3 border-l border-zinc-200 flex flex-col space-y-0.5">
                      {[
                        { to: "/produits/lely", label: "Lely" },
                        { to: "/produits/boumatic", label: "BouMatic" },
                        { to: "/produits/mueller", label: "Mueller" },
                        { to: "/produits/liberchem", label: "Liberchem" },
                      ].map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={handleNavClick}
                          className="px-3 py-2 rounded-md text-sm text-zinc-700 hover:bg-accent/10 hover:text-accent transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </details>

                  <details className="group rounded-lg">
                    <summary className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-900 hover:bg-accent/10 hover:text-accent transition-colors cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                      <Wrench className="h-4 w-4" />
                      <span className="flex-1">Services</span>
                      <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="mt-1 ml-7 pl-3 border-l border-zinc-200 flex flex-col space-y-0.5">
                      {[
                        { to: "/services/assistance", label: "Assistance à la traite" },
                        { to: "/services/inspection", label: "Inspection du système" },
                        { to: "/services/pro-action", label: "Rapports Pro Action" },
                        { to: "/services/urgence", label: "Urgence 24/7" },
                      ].map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={handleNavClick}
                          className="px-3 py-2 rounded-md text-sm text-zinc-700 hover:bg-accent/10 hover:text-accent transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </details>

                  <a
                    href="https://agrostitch.ca/collections"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleNavClick}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-900 hover:bg-accent/10 hover:text-accent transition-colors"
                  >
                    <Store className="h-4 w-4" />
                    Boutique
                  </a>

                  <Link
                    to="/projets"
                    onClick={handleNavClick}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-900 hover:bg-accent/10 hover:text-accent transition-colors"
                  >
                    <Calendar className="h-4 w-4" />
                    Projets & Événements
                  </Link>
                  <Link
                    to="/blogue"
                    onClick={handleNavClick}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-900 hover:bg-accent/10 hover:text-accent transition-colors"
                  >
                    <BookOpen className="h-4 w-4" />
                    Blogue
                  </Link>
                  <Link
                    to="/contact"
                    onClick={handleNavClick}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-900 hover:bg-accent/10 hover:text-accent transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    Contact
                  </Link>
                </nav>
                {user ? (
                  <div className="border-t px-3 py-3">
                    <Button variant="outline" size="sm" onClick={handleLogout} className="w-full">
                      <LogOut className="mr-2 h-4 w-4" />
                      Se déconnecter
                    </Button>
                  </div>
                ) : (
                  <div className="border-t px-3 py-3">
                    <Button size="sm" onClick={() => { navigate("/account"); setIsOpen(false); }} className="w-full bg-accent hover:bg-accent/90">
                      <User className="mr-2 h-4 w-4" />
                      Connexion
                    </Button>
                  </div>
                )}
              </SheetContent>
            </Sheet>

            {user ? (
              <>
                {isProfileComplete ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-full">
                        <User className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => navigate("/account")}>
                        <User className="mr-2 h-4 w-4" />
                        Mon compte
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/order-history")}>
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Mes commandes
                      </DropdownMenuItem>
                      {(isAdmin || isEmployee) && (
                        <>
                          <DropdownMenuSeparator />
                          {hasOrderAccess && (
                            <DropdownMenuItem onClick={() => navigate("/admin/orders")}>
                              <span className="mr-2">📦</span>
                              Admin - Commandes
                            </DropdownMenuItem>
                          )}
                          {hasProductAccess && (
                            <DropdownMenuItem onClick={() => navigate("/admin/products")}>
                              <span className="mr-2">📦</span>
                              Admin - Produits
                            </DropdownMenuItem>
                          )}
                          {hasContentAccess && (
                            <>
                              <DropdownMenuItem onClick={() => navigate("/admin/blogs")}>
                                <span className="mr-2">📝</span>
                                Admin - Blogs
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate("/admin/projects")}>
                                <span className="mr-2">🏗️</span>
                                Admin - Projets
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate("/admin/events")}>
                                <span className="mr-2">📅</span>
                                Admin - Événements
                              </DropdownMenuItem>
                            </>
                          )}
                          {(isAdmin || role === 'moderator') && (
                            <DropdownMenuItem onClick={() => navigate("/admin/contact-messages")}>
                              <span className="mr-2">💬</span>
                              Admin - Messages
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => navigate("/admin/clients")}>
                            <span className="mr-2">👤</span>
                            Admin - Clients
                          </DropdownMenuItem>
                          {isAdmin && (
                            <>
                              <DropdownMenuItem onClick={() => navigate("/admin/employees")}>
                                <span className="mr-2">👥</span>
                                Admin - Employés
                              </DropdownMenuItem>
                            </>
                          )}

                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout}>
                        <LogOut className="mr-2 h-4 w-4" />
                        Se déconnecter
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button variant="ghost" size="sm" onClick={handleLogout} className="text-accent hover:text-accent/90">
                    <LogOut className="mr-2 h-4 w-4" />
                    Se déconnecter
                  </Button>
                )}
              </>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => navigate("/account")}>
                <User className="mr-2 h-4 w-4" />
                Connexion
              </Button>
            )}

          </div>
        </div>
      </div>
    </header>
  );
};

export default NavBar;
